import React, { useState, useEffect } from "react";
import { collection, addDoc, getDocs, query, where, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

export default function SoulCare() {
  const [step, setStep] = useState(1);
  const [rhema, setRhema] = useState("");
  const [guidance, setGuidance] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [entries, setEntries] = useState([]);
  const userId = localStorage.getItem("user_id");

  useEffect(() => {
    if (!userId) return;
    const fetchEntries = async () => {
      const snapshot = await getDocs(
        query(collection(db, "soulCareEntries"), where("user_id", "==", userId))
      );
      const list = snapshot.docs.map(d => {
        const data = d.data();
        return { id: d.id, ...data, created_at: data.created_at?.toDate() || new Date() };
      });
      setEntries(list.sort((a, b) => b.created_at - a.created_at));
    };
    fetchEntries();
  }, [submitted]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const newEntry = {
        user_id: userId,
        rhema,
        guidance,
        created_at: serverTimestamp(),
      };
      const docRef = await addDoc(collection(db, "soulCareEntries"), newEntry);
      setEntries(prev => [{ id: docRef.id, ...newEntry, created_at: new Date() }, ...prev]);
      setSubmitted(true);
      setStep(1);
      setRhema("");
      setGuidance("");
    } catch (err) {
      console.error("Error saving soul care entry:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const reset = () => {
    setStep(1);
    setRhema("");
    setGuidance("");
    setSubmitted(false);
  };

  const boxStyle = {
    background: "rgba(255,255,255,0.85)",
    borderRadius: "20px",
    padding: "30px",
    width: "80%",
    maxWidth: "600px",
    margin: "0 auto 30px",
    boxShadow: "0 4px 16px rgba(13,59,140,0.1)",
  };

  const promptStyle = {
    fontFamily: "Hebrew",
    fontSize: "2rem",
    color: "#0D3B8C",
    lineHeight: "1.6",
    marginBottom: "24px",
  };

  const btnRow = {
    display: "flex",
    gap: "16px",
    justifyContent: "center",
    flexWrap: "wrap",
  };

  return (
    <div style={{ minHeight: "100vh", background: "#F0F7FF", paddingBottom: "60px" }}>
      <br /><br />
      <h2>Soul Care</h2>
      <p className="mediumtext" style={{ fontStyle: "italic" }}>
        A quiet space to bring your heart before God.
      </p>
      <br />

      {/* Step 1 — Are you hurting? */}
      {step === 1 && (
        <div style={boxStyle}>
          <p style={promptStyle}>Are you hurting right now?</p>
          <div style={btnRow}>
            <button className="brownroundbutton" style={{ cursor: "pointer", width: "140px" }} onClick={() => setStep(2)}>
              Yes
            </button>
            <button className="brownroundbutton2" style={{ cursor: "pointer", width: "140px", background: "#F5B300", border: "3px solid #D4770A", color: "#0D3B8C" }} onClick={() => setStep("no")}>
              No
            </button>
          </div>
        </div>
      )}

      {/* Step 2 — Not hurting */}
      {step === "no" && (
        <div style={boxStyle}>
          <p style={{ ...promptStyle, color: "#C8960C" }}>That's great! 🌟</p>
          <p style={promptStyle}>
            You can always come back here if you need Soul Care. May the Lord continue to keep your heart in His peace and joy!
          </p>
          <button className="brownroundbutton" style={{ cursor: "pointer" }} onClick={reset}>
            Start Over
          </button>
        </div>
      )}

      {/* Step 2 — Hurting: prayer prompt */}
      {step === 2 && (
        <div style={boxStyle}>
          <p style={{ ...promptStyle, color: "#D4770A", fontWeight: "bold" }}>
            Take a moment. 🕊️
          </p>
          <p style={promptStyle}>
            Open your heart to the Lord right now. Pour out your tears and invite His presence in. Let Him touch you.
          </p>
          <p style={{ ...promptStyle, fontSize: "1.6rem", color: "#8B4513", fontStyle: "italic" }}>
            Take as long as you need. When you're ready, press Continue.
          </p>
          <button className="brownroundbutton" style={{ cursor: "pointer" }} onClick={() => setStep(3)}>
            Continue
          </button>
        </div>
      )}

      {/* Step 3 — Rhema */}
      {step === 3 && (
        <div style={boxStyle}>
          <p style={{ ...promptStyle, color: "#D4770A", fontWeight: "bold" }}>
            Ask the Holy Spirit for a Rhema. ✨
          </p>
          <p style={promptStyle}>
            Ask the Holy Spirit for a Rhema — a live word from Heaven — that will put your heart back into peace, joy, and love. If you detect something, write it here.
          </p>
          <textarea
            value={rhema}
            onChange={e => setRhema(e.target.value)}
            placeholder="Write what you sense or receive..."
            style={{ width: "90%", padding: "12px", fontSize: "16px", borderRadius: "10px", border: "2px solid #DBEAFE", fontFamily: "Hebrew", marginBottom: "20px", minHeight: "120px" }}
          />
          <br />
          <button className="brownroundbutton" style={{ cursor: "pointer" }} onClick={() => setStep(4)}>
            Continue
          </button>
        </div>
      )}

      {/* Step 4 — Gratitude & guidance */}
      {step === 4 && (
        <div style={boxStyle}>
          <p style={{ ...promptStyle, color: "#D4770A", fontWeight: "bold" }}>
            Give thanks and ask for guidance. 🙏
          </p>
          <p style={promptStyle}>
            Thank Father God, the Lord Jesus, and their loving Spirit for the help, and ask for guidance for next steps. Write anything you detect here.
          </p>
          <textarea
            value={guidance}
            onChange={e => setGuidance(e.target.value)}
            placeholder="Write your thanks and any guidance you sense..."
            style={{ width: "90%", padding: "12px", fontSize: "16px", borderRadius: "10px", border: "2px solid #DBEAFE", fontFamily: "Hebrew", marginBottom: "20px", minHeight: "120px" }}
          />
          <br />
          <button
            className="brownroundbutton"
            style={{ cursor: "pointer" }}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save & Finish"}
          </button>
        </div>
      )}

      {/* Success message */}
      {submitted && (
        <div style={{ ...boxStyle, background: "rgba(245,179,0,0.15)", border: "2px solid #F5B300" }}>
          <p style={{ ...promptStyle, color: "#C8960C", fontWeight: "bold" }}>
            Well done. 💛 Your soul care session has been saved.
          </p>
          <button className="brownroundbutton" style={{ cursor: "pointer" }} onClick={reset}>
            New Session
          </button>
        </div>
      )}

      {/* Saved entries */}
      {entries.length > 0 && (
        <div style={{ width: "80%", maxWidth: "600px", margin: "0 auto" }}>
          <h3 style={{ color: "#0D3B8C", marginBottom: "16px" }}>Your Past Sessions</h3>
          {entries.map(entry => (
            <div key={entry.id} style={{ background: "rgba(255,255,255,0.8)", borderRadius: "16px", padding: "20px", marginBottom: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", textAlign: "left" }}>
              <p style={{ fontSize: "1.3rem", color: "#888", fontStyle: "italic", marginBottom: "10px" }}>
                {new Date(entry.created_at).toLocaleDateString("en-us", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
              </p>
              {entry.rhema && (
                <>
                  <p style={{ fontFamily: "Hebrew", fontSize: "1.5rem", color: "#D4770A", fontWeight: "bold", margin: "0 0 4px" }}>Rhema received:</p>
                  <p style={{ fontFamily: "Hebrew", fontSize: "1.6rem", color: "#0D3B8C", margin: "0 0 14px" }}>{entry.rhema}</p>
                </>
              )}
              {entry.guidance && (
                <>
                  <p style={{ fontFamily: "Hebrew", fontSize: "1.5rem", color: "#D4770A", fontWeight: "bold", margin: "0 0 4px" }}>Guidance detected:</p>
                  <p style={{ fontFamily: "Hebrew", fontSize: "1.6rem", color: "#0D3B8C", margin: 0 }}>{entry.guidance}</p>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
